import { Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ProtectedRoute } from './components';
import Layout from './components/Layout';
import PortalShellContainer from './components/portal/PortalShellContainer';
import { AuthProvider, LanguageProvider, ReservationProvider, useAuth } from './contexts';
import { apiClient } from './lib/api';
import { isWhiteLabelOrigin } from './lib/whiteLabel';
import {
  CheckInForm,
  DamageWaiver,
  DamageWaiverResult,
  Guidebook,
  GuidebookPrint,
  ListingGuidebook,
  Login,
  LoyaltySignup,
  Portal,
  PortalLogin,
  SelfieCapture,
  Sequence,
  SignAgreement,
  Upsells,
  UpsellResult,
  AirbnbInstructions,
} from './pages';

// On white-label origins the bare `/` is the portal login entry. Elsewhere
// (bestystays.com) the current per-reservation Login fallback is preserved so
// existing `/` behavior is unchanged.
function RootRouter() {
  if (isWhiteLabelOrigin()) return <PortalLogin />;
  return <Login />;
}

function AppContent() {
  const { token } = useAuth();
  const { t } = useTranslation('damageWaiver');
  const { t: tPortal } = useTranslation('portal');
  if (token) apiClient.setToken(token);

  return (
    <Routes>
      <Route path="/selfie-capture" element={<SelfieCapture />} />

      {/* had to move layout wrapper to the SignAgreement file because of a styling thing that needed layout stuff in the file */}
      <Route
        path="/sign-agreement"
        element={
          <ProtectedRoute requireScope="rental_agreements">
            <SignAgreement />
          </ProtectedRoute>
        }
      />

      <Route path="/guide/:listingCode" element={<ListingGuidebook />} />
      <Route path="/guidebook-preview" element={<Guidebook />} />
      {/* BES-8422: host-opened printable guidebook. Auth comes from the #token fragment
          minted by POST /api/guidebooks/print-token, handled inside the page. */}
      <Route path="/print/guidebook" element={<GuidebookPrint />} />

      {/* Portal universal-login entry. White-label origins use the bare origin
          root (`/`); bestystays.com uses `/portal/:hostCode`. The `/portal` and
          `/portal/:hostCode` forms remain so old QR codes / issued links keep
          working on both bestystays and white-label domains. */}
      <Route path="/" element={<RootRouter />} />
      <Route path="/portal" element={<PortalLogin />} />
      <Route path="/portal/:hostCode" element={<PortalLogin />} />

      {/* Portal — shell (sidebar + top bar) stays mounted across section transitions
           via nested routes + Outlet, avoiding sidebar re-render on nav. */}
      <Route
        path="/portal/home"
        element={
          <ProtectedRoute requireScope="portal">
            <PortalShellContainer />
          </ProtectedRoute>
        }
      >
        <Route index element={<Portal />} />
        <Route
          path="rental-agreement"
          element={
            <ProtectedRoute requireScope="rental_agreements">
              <SignAgreement />
            </ProtectedRoute>
          }
        />
        <Route
          path="check-in"
          element={
            <ProtectedRoute requireScope="check_in_forms">
              <Layout showReservation title="Check‑in Form" hideTitle>
                <CheckInForm />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="damage-waiver"
          element={
            <ProtectedRoute requireScope="damage_waivers">
              <Layout
                showReservation
                title={t('Protect Your Stay')}
                subtitle={t(
                  "Choose how you'd like to safeguard yourself against being charged for damages during your stay."
                )}
                showTitleBelow
              >
                <DamageWaiver />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="guidebook"
          element={
            <ProtectedRoute requireScope="guidebooks">
              <Guidebook />
            </ProtectedRoute>
          }
        />
        <Route
          path="add-ons"
          element={
            <ProtectedRoute requireScope="add_ons">
              <Layout showReservation title="Add-Ons" hideTitle>
                <Upsells />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="add-on-result"
          element={
            <ProtectedRoute requireScope="add_ons">
              <Layout showReservation hideTitle>
                <UpsellResult />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="add-on-instructions"
          element={
            <ProtectedRoute requireScope="add_ons">
              <Layout showReservation hideTitle>
                <AirbnbInstructions />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="damage-waiver-result"
          element={
            <ProtectedRoute requireScope="damage_waivers">
              <DamageWaiverResult />
            </ProtectedRoute>
          }
        />
        {/* Loyalty page lives inside the shell so the guest navigates here from
            the left nav (or the welcome modal). Reads loyalty state from the
            shell's outlet context — no extra fetch on mount. */}
        <Route path="loyalty" element={<LoyaltySignup />} />
      </Route>

      <Route
        path="/guidebook"
        element={
          <ProtectedRoute requireScope="guidebooks">
            <Guidebook />
          </ProtectedRoute>
        }
      />

      <Route
        path="/check-in"
        element={
          <ProtectedRoute requireScope="check_in_forms">
            <Layout showReservation title="Check‑in Form" hideTitle>
              <CheckInForm />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/damage-waiver"
        element={
          <ProtectedRoute requireScope="damage_waivers">
            <Layout
              showReservation
              title={t('Protect Your Stay')}
              subtitle={t(
                "Choose how you'd like to safeguard yourself against being charged for damages during your stay."
              )}
              showTitleBelow
            >
              <DamageWaiver />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Protected sequence route (requires new 'sequence' scope) */}
      <Route
        path="/pre-arrival-checklist"
        element={
          <ProtectedRoute requireScope="sequence">
            <Layout
              showReservation
              title={tPortal('Complete Your Pre-arrival Tasks')}
              subtitle={tPortal('Finish these steps to get ready for your stay.')}
              hideTitle
            >
              <Sequence />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Damage waiver result page - handles Stripe payment callbacks */}
      <Route
        path="/damage-waiver-result"
        element={
          <ProtectedRoute requireScope="damage_waivers">
            <Layout showReservation hideTitle>
              <DamageWaiverResult embedded />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Upsell add-ons pages */}
      <Route
        path="/add-ons"
        element={
          <ProtectedRoute requireScope="add_ons">
            <Layout showReservation title="Add-Ons" hideTitle>
              <Upsells />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/add-on-result"
        element={
          <ProtectedRoute requireScope="add_ons">
            <Layout showReservation hideTitle>
              <UpsellResult />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/add-on-instructions"
        element={
          <ProtectedRoute requireScope="add_ons">
            <Layout showReservation hideTitle>
              <AirbnbInstructions />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Access code routes with page specification */}
      <Route path="/:code/:page" element={<Login />} />

      {/* Fallback route for invalid patterns */}
      <Route path="*" element={<Login />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ReservationProvider>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </ReservationProvider>
    </AuthProvider>
  );
}

export default App;
